import React, { useState, useEffect } from "react";
import {
  FolderOpen,
  File,
  Mail,
  ChevronRight,
  ChevronDown,
  Trash2,
  X,
  Calendar,
  Paperclip,
  Printer,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { getToken, base_url } from "../config/config";

// -------------------- FileViewer Component --------------------
const FileViewer = ({ file, onClose }) => {
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open("", "_blank");

    // For images
    if (file.file.mimeType.startsWith("image/")) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Drucken: ${file.file.originalName}</title>
            <style>
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
              }
              @media print {
                body {
                  height: auto;
                }
              }
            </style>
          </head>
          <body>
            <img src="${file.file.s3Url}" alt="${file.file.originalName}" />
            <script>
              // Auto print when everything is loaded
              window.onload = function() {
                setTimeout(() => {
                  window.print();
                }, 500);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
    // For PDFs and other documents
    else {
      printWindow.location.href = file.file.s3Url;
      // Give the browser time to load the document before triggering print
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    }
  };

  const getFileComponent = () => {
    // Determine the file type from its MIME type
    const fileType = file.file.mimeType.split("/")[0];

    switch (fileType) {
      case "image":
        return (
          <img
            src={file.file.s3Url}
            alt={file.file.originalName}
            className='max-h-full max-w-full object-contain'
          />
        );
      case "application":
        if (file.file.mimeType === "application/pdf") {
          return (
            <iframe
              src={file.file.s3Url}
              className='w-full h-full'
              title={file.file.originalName}
            />
          );
        }
        return (
          <div className='text-center p-4'>
            <a
              href={file.file.s3Url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-500 hover:text-blue-700 underline'>
              Download {file.file.originalName}
            </a>
          </div>
        );
      default:
        return (
          <div className='text-center p-4'>
            <a
              href={file.file.s3Url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-500 hover:text-blue-700 underline'>
              Download {file.file.originalName}
            </a>
          </div>
        );
    }
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh] m-4 flex flex-col'>
        <div className='p-4 border-b flex justify-between items-center'>
          <h3 className='text-lg font-medium'>{file.file.originalName}</h3>
          <div className='flex items-center gap-2'>
            <button
              onClick={handlePrint}
              className='flex items-center gap-1 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors'>
              <Printer size={16} />
              <span>Drucken</span>
            </button>
            <button
              onClick={onClose}
              className='p-1 hover:bg-gray-100 rounded-full'>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className='flex-1 overflow-auto p-4'>{getFileComponent()}</div>
      </div>
    </div>
  );
};

// -------------------- EmailViewer Component --------------------
const EmailViewer = ({ email, onClose }) => {
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Drucken: ${email.subject}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .email-header {
              margin-bottom: 20px;
              border-bottom: 1px solid #eee;
              padding-bottom: 15px;
            }
            .header-row {
              margin-bottom: 8px;
            }
            .header-label {
              font-weight: bold;
              display: inline-block;
              width: 70px;
            }
            .email-body {
              white-space: pre-wrap;
            }
            .attachments {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid #eee;
            }
            .attachment-title {
              font-weight: bold;
              margin-bottom: 10px;
            }
            .attachment-item {
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          <div class="email-header">
            <div class="header-row">
              <span class="header-label">Von:</span>
              <span>${email.from}</span>
            </div>
            <div class="header-row">
              <span class="header-label">Betreff:</span>
              <span>${email.subject}</span>
            </div>
            <div class="header-row">
              <span class="header-label">Datum:</span>
              <span>${new Date(email.date).toLocaleString()}</span>
            </div>
          </div>

          <div class="email-body">
            ${email.body}
          </div>

          ${
            email.attachments && email.attachments.length > 0
              ? `
            <div class="attachments">
              <div class="attachment-title">Anhänge (${
                email.attachments.length
              }):</div>
              ${email.attachments
                .map(
                  (attachment) =>
                    `<div class="attachment-item">
                  ${attachment.filename} (${Math.round(
                      attachment.size / 1024,
                    )} KB)
                </div>`,
                )
                .join("")}
            </div>
          `
              : ""
          }

          <script>
            window.onload = function() {
              setTimeout(() => {
                window.print();
              }, 500);
            }
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleAttachmentClick = (attachment) => {
    // Create a file-like object that FileViewer can use
    setSelectedAttachment({
      file: {
        mimeType: attachment.contentType,
        originalName: attachment.filename,
        s3Url: `data:${attachment.contentType};base64,${attachment.content}`,
      },
    });
  };

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh] m-4 flex flex-col'>
        <div className='p-4 border-b flex justify-between items-center'>
          <div className='flex items-center gap-2'>
            <Mail className='text-blue-500' />
            <h3 className='text-lg font-medium'>Email Details</h3>
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={handlePrint}
              className='flex items-center gap-1 px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors'>
              <Printer size={16} />
              <span>Drucken</span>
            </button>
            <button
              onClick={onClose}
              className='p-1 hover:bg-gray-100 rounded-full'>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className='flex-1 overflow-auto p-6'>
          <div className='space-y-6'>
            {/* Email Header */}
            <div className='space-y-3'>
              <div className='flex items-start gap-2'>
                <span className='text-gray-500 w-20'>Von:</span>
                <span className='font-medium'>{email.from}</span>
              </div>
              <div className='flex items-start gap-2'>
                <span className='text-gray-500 w-20'>Betreff:</span>
                <span className='font-medium'>{email.subject}</span>
              </div>
              <div className='flex items-start gap-2'>
                <span className='text-gray-500 w-20'>Datum:</span>
                <div className='flex items-center gap-2'>
                  <Calendar size={16} className='text-gray-400' />
                  <span>{new Date(email.date).toLocaleString()}</span>
                </div>
              </div>
            </div>
            {/* Email Body */}
            <div className='border-t pt-6'>
              <div className='prose max-w-none'>
                <p className='whitespace-pre-wrap'>{email.body}</p>
              </div>
            </div>
            {/* Attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <div className='border-t pt-6'>
                <h4 className='text-sm font-medium text-gray-500 mb-4'>
                  Anhänge
                </h4>
                <div className='space-y-2'>
                  {email.attachments.map((attachment, index) => (
                    <button
                      key={index}
                      onClick={() => handleAttachmentClick(attachment)}
                      className='w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left'>
                      <Paperclip size={16} className='text-gray-400' />
                      <span className='text-sm text-gray-600'>
                        {attachment.filename}
                      </span>
                      <span className='text-xs text-gray-400'>
                        ({Math.round(attachment.size / 1024)} KB)
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedAttachment && (
        <FileViewer
          file={selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
        />
      )}
    </div>
  );
};

// Rest of the component stays the same
const AnfrageFileManager = () => {
  const { anf } = useParams();
  const [files, setFiles] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubCategories, setExpandedSubCategories] = useState({});
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const token = getToken();

  useEffect(() => {
    fetchData();
  }, [anf]);

  // Fetch files using the new endpoint (getFilesByAnf)
  const fetchData = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getFilesByAnf?ANF=${anf}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      // Process files to have a consistent structure
      const processedFiles = data.files.map((item) => {
        // Check if the item contains document property (for files)
        if (item.document) {
          return {
            _id: item._id,
            file: item.document,
            category: {
              labelId: item.categoryId.labelId,
              name: item.categoryId.name,
            },
          };
        }
        // For emails or other types
        return item;
      });

      // Separate emails from files (assuming emails have an "email" property)
      const emailData = processedFiles.filter((item) => item.email);
      const fileData = processedFiles.filter((item) => !item.email);

      setFiles(fileData);
      setEmails(emailData);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Daten konnten nicht geladen werden");
      setLoading(false);
    }
  };

  // Delete file using updated endpoint (removeDocumentFromAnfrage)
  const deleteFile = async (file) => {
    try {
      setDeletingFiles((prev) => new Set([...prev, file._id]));
      const response = await fetch(`${base_url}/v1/removeDocumentFromAnfrage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: file._id,
          ANF: anf,
        }),
      });
      if (!response.ok) throw new Error("Löschen fehlgeschlagen");
      await fetchData();
    } catch (err) {
      console.error("Fehler beim Löschen:", err);
      setError("Datei konnte nicht gelöscht werden");
    } finally {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file._id);
        return newSet;
      });
    }
  };

  // Group files by category and subcategory with null checking
  const groupedFiles = files.reduce((acc, file) => {
    // Make sure category and labelId exist before trying to access their properties
    if (!file.category || !file.category.labelId) {
      // If category or labelId is missing, group under "Uncategorized"
      const categoryName = "Uncategorized";
      const subCategoryName = "Other";

      if (!acc[categoryName]) acc[categoryName] = {};
      if (!acc[categoryName][subCategoryName])
        acc[categoryName][subCategoryName] = [];
      acc[categoryName][subCategoryName].push(file);
    } else {
      // Normal case when category structure is complete
      const categoryName = file.category.labelId.name;
      const subCategoryName = file.category.name;

      if (!acc[categoryName]) acc[categoryName] = {};
      if (!acc[categoryName][subCategoryName])
        acc[categoryName][subCategoryName] = [];
      acc[categoryName][subCategoryName].push(file);
    }
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSubCategory = (category, subCategory) => {
    const key = `${category}-${subCategory}`;
    setExpandedSubCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!anf)
    return (
      <div className='flex items-center justify-center h-screen text-gray-600'>
        Kein ANR angegeben
      </div>
    );
  if (loading)
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
      </div>
    );
  if (error) return <div className='text-red-500 p-4'>{error}</div>;

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <h1 className='text-2xl font-bold mb-6 text-gray-800'>
        File Manager – ANR: {anf}
      </h1>

      {/* Emails Section */}
      {emails.length > 0 && (
        <div className='mb-8'>
          <h2 className='text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2'>
            <Mail className='text-blue-500' />
            Emails
          </h2>
          <div className='bg-white rounded-lg shadow-sm p-4'>
            <div className='divide-y'>
              {emails.map((email) => (
                <div key={email._id} className='py-3 first:pt-0 last:pb-0'>
                  <button
                    onClick={() => setSelectedEmail(email)}
                    className='w-full text-left flex items-center gap-4 hover:bg-gray-50 p-2 rounded-lg transition-colors'>
                    <Mail size={20} className='text-gray-400' />
                    <div className='flex-1 min-w-0'>
                      <p className='font-medium text-gray-900 truncate'>
                        {email.email.subject}
                      </p>
                      <p className='text-sm text-gray-500'>
                        {email.email.from}
                      </p>
                    </div>
                    <span className='text-sm text-gray-400'>
                      {new Date(email.email.date).toLocaleDateString()}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Files Section */}
      <div className='space-y-6'>
        {Object.entries(groupedFiles).map(([category, subCategories]) => (
          <div key={category} className='bg-white rounded-lg shadow-sm p-4'>
            <button
              onClick={() => toggleCategory(category)}
              className='flex items-center gap-2 w-full text-left mb-4'>
              {expandedCategories[category] ? (
                <ChevronDown className='text-gray-500' size={20} />
              ) : (
                <ChevronRight className='text-gray-500' size={20} />
              )}
              <FolderOpen className='text-blue-500' size={24} />
              <h2 className='text-lg font-semibold text-gray-700'>
                {category}
              </h2>
              <span className='bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full'>
                {Object.values(subCategories).flat().length} Dateien
              </span>
            </button>

            {expandedCategories[category] && (
              <div className='pl-6 space-y-4'>
                {Object.entries(subCategories).map(([subCategory, files]) => {
                  const subCategoryKey = `${category}-${subCategory}`;
                  return (
                    <div
                      key={subCategory}
                      className='border-l-2 border-gray-200 pl-4'>
                      <button
                        onClick={() => toggleSubCategory(category, subCategory)}
                        className='flex items-center gap-2 w-full text-left mb-2'>
                        {expandedSubCategories[subCategoryKey] ? (
                          <ChevronDown className='text-gray-500' size={16} />
                        ) : (
                          <ChevronRight className='text-gray-500' size={16} />
                        )}
                        <h3 className='text-md font-medium text-gray-600'>
                          {subCategory}
                        </h3>
                        <span className='bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full'>
                          {files.length} Dateien
                        </span>
                      </button>

                      {expandedSubCategories[subCategoryKey] && (
                        <div className='grid grid-cols-1 gap-2 mt-2'>
                          {files.map((file) => (
                            <div
                              key={file._id}
                              className='flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors w-full group'>
                              <button
                                className='flex-1 flex items-center gap-2 text-left'
                                onClick={() => setSelectedFile(file)}>
                                <File size={16} className='text-gray-400' />
                                <span className='text-sm text-gray-600'>
                                  {file.file.originalName}
                                </span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFile(file);
                                }}
                                className='opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-full transition-opacity'
                                disabled={deletingFiles.has(file._id)}>
                                {deletingFiles.has(file._id) ? (
                                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-red-500' />
                                ) : (
                                  <Trash2 size={16} className='text-red-500' />
                                )}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedFile && (
        <FileViewer file={selectedFile} onClose={() => setSelectedFile(null)} />
      )}

      {selectedEmail && (
        <EmailViewer
          email={selectedEmail.email}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
};

export default AnfrageFileManager;
